import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TimelineStudy } from '../types/studyTimeline';

export function useStudyExpandState(
  studies: TimelineStudy[],
  showReportHistory: boolean
) {
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());
  const prevShowRef = useRef(showReportHistory);

  useEffect(() => {
    if (prevShowRef.current !== showReportHistory) {
      setOverrides(new Map());
      prevShowRef.current = showReportHistory;
    }
  }, [showReportHistory]);

  const defaultExpanded = useCallback(
    (study: TimelineStudy): boolean => {
      if (study.reports.length === 0) return false;
      if (study.reports.some((r) => r.status === 'ADDENDUM')) return true;
      return showReportHistory && study.reports.length > 0;
    },
    [showReportHistory]
  );

  const isExpanded = useCallback(
    (study: TimelineStudy): boolean => {
      const override = overrides.get(study.uuid);
      if (override !== undefined) return override;
      return defaultExpanded(study);
    },
    [overrides, defaultExpanded]
  );

  const toggleStudy = useCallback(
    (uuid: string) => {
      setOverrides((prev) => {
        const next = new Map(prev);
        const current = next.get(uuid);
        if (current !== undefined) {
          next.set(uuid, !current);
        } else {
          const study = studies.find((s) => s.uuid === uuid);
          next.set(uuid, study ? !defaultExpanded(study) : false);
        }
        return next;
      });
    },
    [studies, defaultExpanded]
  );

  const expandedMap = useMemo(
    () => new Map(studies.map((s) => [s.uuid, isExpanded(s)])),
    [studies, isExpanded]
  );

  return { expandedMap, toggleStudy };
}
